import { HttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { isCompleteGenerationId } from '../lib/generationIdInput';
import { normalizeWithdrawalPaymentFile } from '../lib/normalizeWithdrawalPaymentFile';
import type { WithdrawalPaymentFileApi } from '../lib/normalizeWithdrawalPaymentFile';
import type {
  WithdrawalPaymentFileDetail,
  WithdrawalPaymentFileDetailResponse,
  WithdrawalPaymentFilesFilters,
  WithdrawalPaymentFilesResponse,
} from '../models';

export class WithdrawalPaymentFilesApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /** GET /withdrawal-payment-files — Listado de ficheros de pago */
  async getPaymentFiles(filters: WithdrawalPaymentFilesFilters = {}): Promise<WithdrawalPaymentFilesResponse> {
    const { page = 1, perPage = 25, payer_bank_id, generation_id, date_from, date_to, sorting } = filters;

    const params: Record<string, string | number> = {
      page,
      per_page: perPage,
      'sorting[column]': sorting?.column ?? 'created_at',
      'sorting[direction]': sorting?.direction ?? 'desc',
    };

    if (payer_bank_id) {
      params.payer_bank_id = payer_bank_id;
    }
    const trimmedGenerationId = generation_id?.trim();
    if (trimmedGenerationId && isCompleteGenerationId(trimmedGenerationId)) {
      params.generation_id = trimmedGenerationId.toLowerCase();
    }
    if (date_from) {
      params.date_from = date_from;
    }
    if (date_to) {
      params.date_to = date_to;
    }

    const response = await this.httpClient.get<WithdrawalPaymentFilesResponse & { data: WithdrawalPaymentFileApi[] }>(
      '/withdrawal-payment-files',
      params,
      {},
      true,
    );

    return {
      ...response,
      data: (response.data ?? []).map(normalizeWithdrawalPaymentFile),
    };
  }

  /** GET /withdrawal-payment-files/{id} — Detalle de fichero */
  async getPaymentFileDetail(id: number): Promise<WithdrawalPaymentFileDetailResponse> {
    const response = await this.httpClient.get<WithdrawalPaymentFileDetailResponse | WithdrawalPaymentFileApi>(
      `/withdrawal-payment-files/${id}`,
      {},
      {},
      true,
    );

    const body = (response as WithdrawalPaymentFileDetailResponse)?.data ?? response;
    const normalized = normalizeWithdrawalPaymentFile(body as WithdrawalPaymentFileApi) as WithdrawalPaymentFileDetail;

    if ((response as WithdrawalPaymentFileDetailResponse)?.data !== undefined) {
      return { data: normalized };
    }

    return { data: normalized };
  }

  /** GET /withdrawal-payment-files/{id}/download — Descarga XLSX */
  async downloadPaymentFile(id: number): Promise<
    | { blob: Blob; filename: string }
    | {
        url: string;
        filename: string;
      }
  > {
    const response = await apiClientWH.get<Blob>(`/withdrawal-payment-files/${id}/download`, {
      responseType: 'blob',
    });

    const contentType = (response.headers?.['content-type'] as string | undefined) ?? '';
    const contentDisposition = response.headers['content-disposition'] || '';

    const filenameFromContentDisposition = (() => {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      return filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : undefined;
    })();

    // Backend puede devolver JSON con una URL firmada/externa para descargar.
    // En ese caso, NO debemos crear un blob con el JSON; hay que hacer redirect a `url`.
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      try {
        const parsed = JSON.parse(text) as { url?: string; filename?: string };
        if (parsed?.url) {
          const filename =
            parsed.filename ||
            filenameFromContentDisposition ||
            new URL(parsed.url).pathname.split('/').filter(Boolean).pop() ||
            `withdrawal-payment-file-${id}.xlsx`;
          return { url: parsed.url, filename };
        }
      } catch {
        // Si el JSON no es el esperado, caemos al flujo de blob normal.
      }
    }

    const blob = response.data;
    const filename = filenameFromContentDisposition || `withdrawal-payment-file-${id}.xlsx`;

    return { blob, filename };
  }
}
